/**
 * InvoicePeek (PEEK-INV, Seth 2026-06-16) — clicking an invoice (a property-
 * timeline install, a history row, a membership charge) slides the invoice in
 * from the right instead of leaving the page. Shows the header + the LINE ITEMS
 * (line_desc = what was done / installed) so the "what happened" is readable at
 * a glance — the fast-history experience Seth wants (ESC Mobile's slow drill is
 * the #1 tech complaint). "Open full invoice" is the deliberate deeper dig.
 *
 * Mirrors DispatchPeek: one SlideOver, the shared invoice-detail fetch.
 */
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Receipt, ClipboardList } from "lucide-react";
import { format, parseISO } from "date-fns";
import { SlideOver } from "@/components/ui/SlideOver";
import { Button } from "@/components/ui/Button";
import { useInvoice, useInvoiceLines } from "@/features/invoice-detail/api";
import { deptLabel } from "@/lib/departments";

const money = (n: number | null) =>
  n == null ? "" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export function InvoicePeek({
  invoice,
  onClose,
}: {
  invoice: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: inv, isLoading, isError } = useInvoice(invoice ?? undefined);
  const { data: lines } = useInvoiceLines(invoice ?? undefined);

  let dateLabel = inv?.inv_date ?? "";
  try {
    if (inv?.inv_date) dateLabel = format(parseISO(inv.inv_date), "MMM d, yyyy");
  } catch {
    /* keep raw */
  }
  const shown = (lines ?? []).filter((l) => !l.no_print && (l.line_desc || l.prod));

  return (
    <SlideOver
      open={!!invoice}
      onClose={onClose}
      title={inv ? `Invoice ${inv.invoice}` : "Invoice"}
      subtitle={inv ? `${dateLabel}${inv.inv_amount != null ? ` · ${money(inv.inv_amount)}` : ""}` : undefined}
      size="md"
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="secondary" size="sm" onClick={() => { if (invoice) navigate(`/invoice/${invoice}`); }}>
            Open full invoice
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {isError ? (
        /* 360-AUDIT #18: an error looked like eternal skeletons. */
        <p className="rounded-md bg-rose-50/60 px-3 py-2.5 text-sm font-medium text-rose-700">
          Couldn&apos;t load this — close and reopen to retry.
        </p>
      ) : isLoading || !inv ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Meta pills */}
          <div className="flex flex-wrap items-center gap-2">
            {inv.inv_type && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Receipt className="h-3 w-3" /> {inv.inv_type}
              </span>
            )}
            {inv.dept && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                {deptLabel(inv.dept)}
              </span>
            )}
            {inv.dispatch && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                Dispatch #{inv.dispatch}
              </span>
            )}
          </div>

          {/* Line items — what was done / installed */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <ClipboardList className="h-3 w-3" /> What we did
            </div>
            {shown.length === 0 ? (
              <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
                No line detail on this invoice — open the full invoice for the AR record.
              </p>
            ) : (
              <ul className="divide-y divide-hairline rounded-md border border-hairline">
                {shown.map((l) => (
                  <li key={l.line_count} className="flex items-start justify-between gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800">{l.line_desc || l.prod}</div>
                      {l.quan != null && l.quan !== 1 && (
                        <div className="text-[11px] text-slate-400">qty {l.quan}</div>
                      )}
                    </div>
                    {l.amount != null && l.amount !== 0 && (
                      <div className="shrink-0 text-sm tabular-nums text-slate-700">{money(l.amount)}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {inv.inv_amount != null && (
            <div className="flex items-center justify-between border-t border-hairline pt-2 text-sm">
              <span className="font-medium text-slate-500">Invoice total</span>
              <span className="font-semibold tabular-nums text-slate-900">{money(inv.inv_amount)}</span>
            </div>
          )}
        </div>
      )}
    </SlideOver>
  );
}
